import { matches } from '../criteria'
import { SECTIONS } from '../sections'

// The parcel page is a grid of boxes; each box is a list of parts, and a part
// is a section from the section registry. Rules live on the parts (their
// criteria in sections/index.js): a box has none of its own and is shown when
// at least one of its parts matches. Box order and part order are data, so
// reordering the page means reordering these arrays.
//
// As in the section registry, the page calls each part's `component` as a plain
// function inside its own wrapper, so a body must be a function component:
// memo(), forwardRef() and class components throw on first render. A body must
// also run all its hooks before any early `return null`, since the same wrapper
// outlives the null.
const BOXES = [
  // `fullWidth` is the page's layout in data too: a flagged box spans both
  // columns where it stands instead of taking one cell, so it owns its row
  // without leaving its place in this order. Moving or adding such a row is an
  // edit to this array and nothing else.
  // The log table has a free-text column that wraps to three lines in half a
  // width, and the barcode is scanned off the screen, so both own their row.
  // `white` is the other half of the barcode's case: the image brings its own
  // white background, and the theme's paper is grey behind it.
  {
    id: 'parcel',
    titleKey: 'boxes.parcel',
    parts: ['parcelFacts', 'identity', 'signatures'],
  },
  {
    id: 'location',
    titleKey: 'boxes.location',
    parts: ['locationFacts', 'access'],
  },
  { id: 'status', titleKey: 'boxes.status', parts: ['statuses'] },
  {
    id: 'compartment',
    titleKey: 'boxes.compartment',
    parts: ['reallocate', 'changeBoxSize', 'booking'],
  },
  { id: 'logs', titleKey: 'boxes.logs', fullWidth: true, parts: ['logs'] },
  { id: 'errors', titleKey: 'boxes.errors', parts: ['errors'] },
  { id: 'repair', titleKey: 'boxes.repair', parts: ['repairActions'] },
  {
    id: 'support',
    titleKey: 'boxes.support',
    parts: ['supportNote', 'feedback'],
  },
  { id: 'barcode', fullWidth: true, white: true, parts: ['barcode'] },
]

const sectionById = (id) => {
  const section = SECTIONS.find((candidate) => candidate.id === id)
  if (!section) throw new Error(`Box names unknown section "${id}"`)
  return section
}

// visibleBoxes(context) returns the boxes that have at least one matching part,
// in BOXES order, each as
// { id, titleKey, fullWidth, white, parts: [{ id, component }] }. A box with no title
// (barcode) carries no titleKey key at all, rather than an undefined one, so
// the page only has to check whether a title exists; fullWidth is carried the
// same way, so an ordinary box has no such key.
const visibleBoxes = (context) =>
  BOXES.map((box) => ({
    id: box.id,
    ...(box.titleKey && { titleKey: box.titleKey }),
    ...(box.fullWidth && { fullWidth: true }),
    ...(box.white && { white: true }),
    parts: box.parts
      .map(sectionById)
      .filter((section) => matches(section.criteria, context))
      .map(({ id, component }) => ({ id, component })),
  })).filter((box) => box.parts.length > 0)

export { BOXES, visibleBoxes }
